import React, { SetStateAction } from 'react';

import { FormControl, RadioGroup, Typography } from '@mui/material';
import { Button } from '@apigrowfinance/pbolt-button';
import { PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { CustomersItem } from '@clients/customersClient';
import { useAccount } from '@contexts/AccountProvider';
import ControlRadioBox from '@pages/accountDetail/tabs/controls/addControls/ControlRadioBox';
import getEnumTranslation from '@utils/getEnumTranslation';

interface ControlTypeFormProps {
    customers: CustomersItem[];
    setControlTypeModalOpen: React.Dispatch<SetStateAction<boolean>>;
}

type ControlTypeFormData = {
    controlType: string;
};

const ControlTypeForm = ({
    customers,
    setControlTypeModalOpen,
}: ControlTypeFormProps) => {
    const {
        control,
        handleSubmit,
        formState: { isDirty },
    } = useForm();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { account } = useAccount();
    const accountId = account?.account_id;

    const onSubmit = (data: ControlTypeFormData) => {
        if (data.controlType.includes('-')) {
            navigate(
                `/${accountId}/flex-controls/customers/${
                    data.controlType.split('-')[0]
                }/card/create`,
            );
        } else if (data.controlType === 'account') {
            navigate(`/${accountId}/flex-controls/account/create`);
        } else {
            navigate(
                `/${accountId}/flex-controls/customers/${data.controlType}/create`,
            );
        }
    };

    return (
        <form data-testid="control-type-form" onSubmit={handleSubmit(onSubmit)}>
            <PDialogContent>
                <Controller
                    name="controlType"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <FormControl>
                            <RadioGroup
                                aria-labelledby="control-type-radio-buttons"
                                name="control-type-radio-buttons"
                                onChange={(event, value) => {
                                    onChange(value);
                                }}>
                                <Typography
                                    data-testid="account-control-label"
                                    variant="h6"
                                    sx={{
                                        color: theme?.custom?.brand?.textMain,
                                        mb: 1,
                                    }}>
                                    <FormattedMessage
                                        id="account"
                                        defaultMessage="Account"
                                    />
                                </Typography>
                                <ControlRadioBox
                                    onChange={onChange}
                                    formValue={value}
                                    label="account"
                                    translation="ControlTypeForm.account"
                                    defaultMessage="Account"
                                />
                                {customers?.map((customer) => {
                                    return (
                                        <React.Fragment
                                            key={customer?.customer?.id}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: theme?.custom?.brand
                                                        ?.textMain,
                                                    mb: 1,
                                                }}>
                                                {
                                                    customer?.customer
                                                        ?.printed_name
                                                }
                                            </Typography>
                                            <ControlRadioBox
                                                onChange={onChange}
                                                formValue={value}
                                                label={customer?.customer?.id.toString()}
                                                translation="ControlTypeForm.customer"
                                                defaultMessage="Customer"
                                            />
                                            <ControlRadioBox
                                                onChange={onChange}
                                                formValue={value}
                                                label={`${customer?.customer?.id}-card`}
                                                translation="ControlTypeForm.card"
                                                defaultMessage="Card"
                                            />
                                        </React.Fragment>
                                    );
                                })}
                            </RadioGroup>
                        </FormControl>
                    )}
                />
                <PDialogAction>
                    <Button
                        onClick={() => {
                            setControlTypeModalOpen(false);
                        }}>
                        {getEnumTranslation('cancel')}
                    </Button>
                    <Button
                        disabled={!isDirty}
                        type="submit"
                        variant="contained">
                        {getEnumTranslation('next')}
                    </Button>
                </PDialogAction>
            </PDialogContent>
        </form>
    );
};

export default ControlTypeForm;
